import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIaJobs } from '../../context/IaJobsProvider';
import { useOnboardingPack } from '../../context/OnboardingPackProvider';
import { phaseLabel } from '../../constants/iaJobPhases';
import { IA_PENDING_MICRO_LINES } from '../../constants/iaPendingCopy';

function IaDot({ className = '' }) {
  return (
    <span
      className={`h-[6px] w-[6px] shrink-0 rounded-full bg-[var(--ludi-ai-acc)] shadow-[0_0_6px_var(--ludi-ai-acc)] animate-pulse ${className}`}
      aria-hidden
    />
  );
}

function OnboardingReviewBar({ review, onOpen, onDismiss }) {
  const label = review.count === 1 ? '1 treino pronto' : `${review.count} treinos prontos`;
  return (
    <div className="border-b border-ludi-emp/30 bg-[var(--ludi-nb)] backdrop-blur-ludi">
      <div className="mx-auto flex max-w-app min-h-touch w-full items-center gap-ludi-3 px-ludi-4 py-ludi-3">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-ludi-3 text-left" onClick={onOpen}>
          <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-ludi-emp" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate text-ludi-micro font-bold uppercase tracking-widest text-ludi-emp">
              Treinos sugeridos
            </p>
            <p className="truncate text-ludi-caption text-ludi-t100">
              {label} · <span className="text-ludi-emp">Rever</span>
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="shrink-0 px-1 text-ludi-micro text-ludi-t60 hover:text-ludi-t100"
          aria-label="Ocultar barra de revisão"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function FeedbackReviewBar({ review, onOpen, onDismiss }) {
  return (
    <div className="border-b border-[var(--ludi-ai-bdr)] bg-[var(--ludi-nb)] backdrop-blur-ludi">
      <div className="mx-auto flex max-w-app min-h-touch w-full items-center gap-ludi-3 px-ludi-4 py-ludi-3">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-ludi-3 text-left" onClick={onOpen}>
          <IaDot />
          <div className="min-w-0 flex-1">
            <p className="truncate text-ludi-micro font-bold uppercase tracking-widest text-[var(--ludi-ai-acc)]">
              Análise pronta
            </p>
            <p className="truncate text-ludi-caption text-ludi-t100">
              {review.contextLabel || 'Feedback IA'} · <span className="text-ludi-emp">Rever</span>
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="shrink-0 px-1 text-ludi-micro text-ludi-t60 hover:text-ludi-t100"
          aria-label="Ocultar barra de análise"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function itemSortKey(item) {
  return item.startedAt || item.updatedAt || 0;
}

export default function LudiIaStatusBar() {
  const {
    jobs,
    pendingRequests,
    jobLabel,
    onboardingReview,
    openOnboardingReview,
    feedbackReview,
    openFeedbackReview,
    clearFeedbackReview,
    hasIaBarActivity,
  } = useIaJobs();
  const { clearDraft: clearOnboardingPackDraft } = useOnboardingPack();
  const [expanded, setExpanded] = useState(false);
  const [microIdx, setMicroIdx] = useState(0);
  const rootRef = useRef(null);

  const dismissOnboardingReview = () => {
    clearOnboardingPackDraft();
  };

  const loadingItems = useMemo(() => {
    const jobItems = jobs.map((j) => ({
      kind: 'job',
      id: String(j.id),
      label: jobLabel(j),
      phase: j.phase || 'recebidos',
      engineLabel: null,
      startedAt: j.updatedAt ? new Date(j.updatedAt).getTime() : Date.now(),
    }));
    const pendingItems = pendingRequests.map((p) => ({
      kind: 'pending',
      id: p.id,
      label: p.label,
      phase: p.phase || 'recebidos',
      engineLabel: p.engineLabel,
      startedAt: p.startedAt || Date.now(),
    }));
    return [...jobItems, ...pendingItems].sort((a, b) => itemSortKey(b) - itemSortKey(a));
  }, [jobs, pendingRequests, jobLabel]);

  const hasLoading = loadingItems.length > 0;
  const hasOnboardingReview = onboardingReview != null && onboardingReview.count > 0;
  const hasFeedbackReview = feedbackReview != null && feedbackReview.markdown?.trim();
  const primary = loadingItems[0] || null;
  const secondary = loadingItems.slice(1);

  useEffect(() => {
    if (!hasLoading) return undefined;
    const t = setInterval(() => {
      setMicroIdx((i) => (i + 1) % IA_PENDING_MICRO_LINES.length);
    }, 3200);
    return () => clearInterval(t);
  }, [hasLoading]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !hasIaBarActivity) {
      document.documentElement.style.setProperty('--ludi-ia-bar-h', '0px');
      return undefined;
    }
    const sync = () => {
      document.documentElement.style.setProperty('--ludi-ia-bar-h', `${el.offsetHeight}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty('--ludi-ia-bar-h', '0px');
    };
  }, [hasIaBarActivity, expanded, hasLoading, hasOnboardingReview, hasFeedbackReview]);

  if (!hasIaBarActivity) return null;

  if (!hasLoading && hasFeedbackReview && !hasOnboardingReview) {
    return (
      <div ref={rootRef} className="fixed inset-x-0 top-0 z-[140]">
        <FeedbackReviewBar
          review={feedbackReview}
          onOpen={openFeedbackReview}
          onDismiss={clearFeedbackReview}
        />
      </div>
    );
  }

  if (!hasLoading && hasOnboardingReview && !hasFeedbackReview) {
    return (
      <div ref={rootRef} className="fixed inset-x-0 top-0 z-[140]">
        <OnboardingReviewBar
          review={onboardingReview}
          onOpen={openOnboardingReview}
          onDismiss={dismissOnboardingReview}
        />
      </div>
    );
  }

  return (
    <div ref={rootRef} className="fixed inset-x-0 top-0 z-[140]">
      {hasLoading && primary ? (
        <div
          className="border-b border-[var(--ludi-ai-bdr)] bg-[var(--ludi-nb)] backdrop-blur-ludi"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <button
            type="button"
            className="mx-auto flex max-w-app min-h-touch w-full items-center gap-ludi-3 px-ludi-4 py-ludi-3 text-left"
            onClick={() => secondary.length > 0 && setExpanded((v) => !v)}
            aria-expanded={expanded}
            disabled={secondary.length === 0}
          >
            <IaDot />
            <div className="min-w-0 flex-1">
              <p className="truncate text-ludi-micro font-bold uppercase tracking-widest text-[var(--ludi-ai-acc)]">
                IA
                {primary.engineLabel ? ` · ${primary.engineLabel}` : ''}
              </p>
              <p className="truncate text-ludi-caption text-ludi-t100">{primary.label}</p>
              <p className="truncate text-ludi-sm text-ludi-t60">
                {phaseLabel(primary.phase)} · {IA_PENDING_MICRO_LINES[microIdx]}
              </p>
            </div>
            {secondary.length > 0 ? (
              <span className="text-ludi-micro text-ludi-t60">{expanded ? '▴' : '▾'}</span>
            ) : null}
          </button>
          {expanded && secondary.length > 0 ? (
            <ul className="mx-auto max-w-app space-y-2 border-t border-ludi-bdr-m/40 px-ludi-4 py-ludi-2">
              {secondary.map((item) => (
                <li key={`${item.kind}-${item.id}`} className="flex items-center justify-between gap-2">
                  <span className="truncate text-ludi-caption text-ludi-t60">{item.label}</span>
                  <span className="shrink-0 text-ludi-micro text-[var(--ludi-ai-acc)]">
                    {phaseLabel(item.phase)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {hasFeedbackReview ? (
        <FeedbackReviewBar
          review={feedbackReview}
          onOpen={openFeedbackReview}
          onDismiss={clearFeedbackReview}
        />
      ) : null}
      {hasOnboardingReview ? (
        <OnboardingReviewBar
          review={onboardingReview}
          onOpen={openOnboardingReview}
          onDismiss={dismissOnboardingReview}
        />
      ) : null}
    </div>
  );
}
